import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Download, Filter, RefreshCw, AlertCircle, Users, FileText, FileSpreadsheet,
  DollarSign, AlertTriangle, Clock, CheckCircle, XCircle, ChevronDown, ChevronRight, Search
} from 'lucide-react';
import { reportesApi } from '../../utils/api';
import useAuthStore from '../../store/authStore';
import clsx from 'clsx';
import toast from 'react-hot-toast';

const RIESGO_CONFIG = {
  normal: { label: 'Normal', color: 'bg-green-100 text-green-700', dot: 'bg-green-500' },
  bajo: { label: 'Bajo', color: 'bg-yellow-100 text-yellow-700', dot: 'bg-yellow-500' },
  medio: { label: 'Medio', color: 'bg-orange-100 text-orange-700', dot: 'bg-orange-500' },
  alto: { label: 'Alto', color: 'bg-red-100 text-red-700', dot: 'bg-red-500' }
};

export default function CuentasCobrar() {
  const { currentEmisor } = useAuthStore();
  // Prefijo "Nombre de la empresa - " para los archivos descargados (sin caracteres inválidos)
  const empresaPrefix = ((currentEmisor?.nombreComercial || currentEmisor?.nombre || 'Empresa')
    .replace(/[\\/:*?"<>|]/g, '').replace(/\s+/g, ' ').trim()) + ' - ';
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [fechaCorte, setFechaCorte] = useState(new Date().toISOString().split('T')[0]);
  const [clienteExpandido, setClienteExpandido] = useState(null);
  const [busquedaCliente, setBusquedaCliente] = useState('');

  const cargarReporte = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await reportesApi.getCuentasCobrar({ fechaCorte });
      setData(response.data.data);
    } catch (err) {
      console.error('Error cargando reporte:', err);
      setError(err.response?.data?.error || 'Error al cargar el reporte');
      toast.error('Error al cargar el reporte');
    } finally {
      setLoading(false);
    }
  };

  const exportarExcel = async () => {
    setExporting(true);
    try {
      const response = await reportesApi.exportCuentasCobrar({ fechaCorte });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${empresaPrefix}Cuentas_por_Cobrar_${fechaCorte}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Reporte exportado exitosamente');
    } catch (err) {
      console.error('Error exportando:', err);
      toast.error('Error al exportar el reporte');
    } finally {
      setExporting(false);
    }
  };

  const exportarPdf = async () => {
    setExportingPdf(true);
    try {
      const response = await reportesApi.exportCuentasCobrarPdf({ fechaCorte });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${empresaPrefix}Cuentas_por_Cobrar_${fechaCorte}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('PDF exportado exitosamente');
    } catch (err) {
      console.error('Error exportando PDF:', err);
      toast.error('Error al exportar el PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  useEffect(() => {
    cargarReporte();
  }, []);

  const formatUSD = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatCRC = (amount) => {
    return new Intl.NumberFormat('es-CR', {
      style: 'currency',
      currency: 'CRC',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-CR');
  };

  const toggleCliente = (clienteId) => {
    setClienteExpandido(clienteExpandido === clienteId ? null : clienteId);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Cuentas por Cobrar</h1>
          <p className="text-gray-500 mt-1">Antigüedad de saldos y estado de cartera</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={exportarPdf}
            disabled={exportingPdf || !data || data.clientes?.length === 0}
            className={clsx(
              'flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors',
              exportingPdf || !data || data.clientes?.length === 0
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'border-red-500 text-red-600 hover:bg-red-50'
            )}
          >
            {exportingPdf ? <RefreshCw className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
            PDF
          </button>
          <button
            onClick={exportarExcel}
            disabled={exporting || !data || data.clientes?.length === 0}
            className={clsx(
              'flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors',
              exporting || !data || data.clientes?.length === 0
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'border-green-500 text-green-600 hover:bg-green-50'
            )}
          >
            {exporting ? <RefreshCw className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
            Excel
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de Corte</label>
            <input
              type="date"
              value={fechaCorte}
              onChange={(e) => setFechaCorte(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <button
            onClick={cargarReporte}
            disabled={loading}
            className="flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Filter className="h-4 w-4" />}
            Consultar
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-500" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 text-primary-600 animate-spin" />
        </div>
      )}

      {!loading && data && (
        <>
          {/* Distribución por Antigüedad - Siempre mostrar ambas monedas */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl shadow-sm border border-blue-200 p-5"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded text-xs font-bold bg-blue-500 text-white">USD</span>
                  Distribución USD
                </h3>
                <div className="text-right">
                  <p className="text-xs text-gray-500">Total Cartera</p>
                  <p className="text-lg font-bold text-blue-600">{formatUSD(data.resumenUSD?.totalCartera)}</p>
                  <p className="text-xs text-gray-400">{data.resumenUSD?.totalFacturas || 0} facturas</p>
                </div>
              </div>
              <div className="space-y-3">
                {data.distribucionUSD?.map((item, index) => {
                  const total = data.resumenUSD?.totalCartera || 1;
                  const percentage = total > 0 ? (item.monto / total) * 100 : 0;
                  return (
                    <div key={index} className="flex items-center gap-3">
                      <div className="w-20 text-xs font-medium text-gray-600">{item.bucket}</div>
                      <div className="flex-1">
                        <div className="h-4 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${percentage > 0 ? Math.max(percentage, 2) : 0}%`, backgroundColor: item.color }} />
                        </div>
                      </div>
                      <div className="w-24 text-right text-sm font-semibold text-gray-900">{formatUSD(item.monto)}</div>
                    </div>
                  );
                })}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-5"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded text-xs font-bold bg-gray-500 text-white">CRC</span>
                  Distribución CRC
                </h3>
                <div className="text-right">
                  <p className="text-xs text-gray-500">Total Cartera</p>
                  <p className="text-lg font-bold text-gray-700">{formatCRC(data.resumenCRC?.totalCartera)}</p>
                  <p className="text-xs text-gray-400">{data.resumenCRC?.totalFacturas || 0} facturas</p>
                </div>
              </div>
              <div className="space-y-3">
                {data.distribucionCRC?.map((item, index) => {
                  const total = data.resumenCRC?.totalCartera || 1;
                  const percentage = total > 0 ? (item.monto / total) * 100 : 0;
                  return (
                    <div key={index} className="flex items-center gap-3">
                      <div className="w-20 text-xs font-medium text-gray-600">{item.bucket}</div>
                      <div className="flex-1">
                        <div className="h-4 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${percentage > 0 ? Math.max(percentage, 2) : 0}%`, backgroundColor: item.color }} />
                        </div>
                      </div>
                      <div className="w-28 text-right text-sm font-semibold text-gray-900">{formatCRC(item.monto)}</div>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          </div>

          {/* Top 5 Deudores */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-white rounded-xl shadow-sm border border-gray-100 p-5"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Top 5 Deudores</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {data.top5.map((cliente, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <span className="w-8 h-8 rounded-full bg-red-100 text-red-700 flex items-center justify-center text-sm font-bold flex-shrink-0">
                    {index + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900">{cliente.nombre}</p>
                    <div className="text-xs">
                      {cliente.saldoUSD > 0 && (
                        <span className="font-bold text-blue-600">{formatUSD(cliente.saldoUSD)}</span>
                      )}
                      {cliente.saldoUSD > 0 && cliente.saldoCRC > 0 && <span className="text-gray-400 mx-1">|</span>}
                      {cliente.saldoCRC > 0 && (
                        <span className="font-bold text-gray-600">{formatCRC(cliente.saldoCRC)}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Tabla de Clientes con Expandible */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="bg-white rounded-xl shadow-sm border border-gray-100"
          >
            <div className="p-4 border-b border-gray-100 flex items-center gap-4">
              <h3 className="text-lg font-semibold text-gray-900">Detalle por Cliente</h3>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar cliente..."
                  value={busquedaCliente}
                  onChange={(e) => setBusquedaCliente(e.target.value)}
                  className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-80"
                />
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr className="text-left text-sm text-gray-500">
                    <th className="px-4 py-3 font-medium w-10"></th>
                    <th className="px-4 py-3 font-medium">Cliente</th>
                    <th className="px-4 py-3 font-medium text-center">Facturas</th>
                    <th className="px-4 py-3 font-medium text-right">Saldo USD</th>
                    <th className="px-4 py-3 font-medium text-right">Saldo CRC</th>
                    <th className="px-4 py-3 font-medium text-center">Riesgo</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.clientes
                    .filter(cliente =>
                      !busquedaCliente ||
                      cliente.clienteNombre?.toLowerCase().includes(busquedaCliente.toLowerCase()) ||
                      cliente.clienteIdentificacion?.includes(busquedaCliente)
                    )
                    .map((cliente) => (
                    <>
                      <tr
                        key={cliente.clienteId}
                        className="text-sm hover:bg-gray-50 cursor-pointer"
                        onClick={() => toggleCliente(cliente.clienteId)}
                      >
                        <td className="px-4 py-3">
                          {clienteExpandido === cliente.clienteId
                            ? <ChevronDown className="h-4 w-4 text-gray-400" />
                            : <ChevronRight className="h-4 w-4 text-gray-400" />
                          }
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-gray-900">{cliente.clienteNombre}</p>
                            <p className="text-xs text-gray-500">{cliente.clienteIdentificacion}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center text-gray-600">{cliente.totalFacturas}</td>
                        <td className="px-4 py-3 text-right font-bold text-blue-600">
                          {cliente.saldoUSD > 0 ? formatUSD(cliente.saldoUSD) : '-'}
                        </td>
                        <td className="px-4 py-3 text-right font-bold text-gray-700">
                          {cliente.saldoCRC > 0 ? formatCRC(cliente.saldoCRC) : '-'}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={clsx(
                            'px-2 py-1 rounded-full text-xs font-medium',
                            RIESGO_CONFIG[cliente.riesgo]?.color || 'bg-gray-100 text-gray-700'
                          )}>
                            {RIESGO_CONFIG[cliente.riesgo]?.label || 'Normal'}
                          </span>
                        </td>
                      </tr>
                      {/* Detalle de facturas expandido */}
                      {clienteExpandido === cliente.clienteId && (
                        <tr>
                          <td colSpan="6" className="bg-gray-50 px-8 py-4">
                            <div className="text-sm">
                              <p className="font-medium text-gray-700 mb-3">Facturas pendientes:</p>
                              <table className="w-full">
                                <thead>
                                  <tr className="text-xs text-gray-500">
                                    <th className="text-left pb-2">Consecutivo</th>
                                    <th className="text-left pb-2">Fecha</th>
                                    <th className="text-center pb-2">Moneda</th>
                                    <th className="text-right pb-2">Total</th>
                                    <th className="text-right pb-2">Pagado</th>
                                    <th className="text-right pb-2">Saldo</th>
                                    <th className="text-center pb-2">Días</th>
                                    <th className="text-center pb-2">Estado</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {cliente.facturas.map((f, idx) => {
                                    const formatFn = f.moneda === 'USD' ? formatUSD : formatCRC;
                                    return (
                                      <tr key={idx} className="border-t border-gray-200">
                                        <td className="py-2 font-mono text-xs">{f.consecutivo}</td>
                                        <td className="py-2">{formatDate(f.fechaEmision)}</td>
                                        <td className="py-2 text-center">
                                          <span className={`px-2 py-0.5 rounded text-xs font-bold ${f.moneda === 'USD' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>
                                            {f.moneda}
                                          </span>
                                        </td>
                                        <td className="py-2 text-right">{formatFn(f.totalOriginal)}</td>
                                        <td className="py-2 text-right text-green-600">{formatFn(f.totalPagado)}</td>
                                        <td className="py-2 text-right font-medium">{formatFn(f.saldo)}</td>
                                        <td className="py-2 text-center">{f.diasVencido > 0 ? f.diasVencido : '-'}</td>
                                        <td className="py-2 text-center">
                                          <span className={clsx(
                                            'px-2 py-0.5 rounded text-xs',
                                            f.bucket === 'corriente' ? 'bg-green-100 text-green-700' :
                                            f.bucket === '1-30' ? 'bg-yellow-100 text-yellow-700' :
                                            f.bucket === '31-60' ? 'bg-orange-100 text-orange-700' :
                                            f.bucket === '61-90' ? 'bg-red-100 text-red-700' :
                                            'bg-red-200 text-red-800'
                                          )}>
                                            {f.bucket === 'corriente' ? 'No Vencido' : f.bucket + ' días'}
                                          </span>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
            {data.clientes.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No hay cuentas por cobrar pendientes
              </div>
            )}
            {data.clientes.length > 0 && busquedaCliente && !data.clientes.some(c =>
              c.clienteNombre?.toLowerCase().includes(busquedaCliente.toLowerCase()) ||
              c.clienteIdentificacion?.includes(busquedaCliente)
            ) && (
              <div className="text-center py-8 text-gray-500">
                No se encontraron clientes con "{busquedaCliente}"
              </div>
            )}
          </motion.div>
        </>
      )}
    </div>
  );
}
